import {FC} from 'react';
import {type IBrand} from '@verdocs/js-sdk';
import {VerdocsButton} from '@verdocs/web-sdk-react';
import {CheckCircleIcon} from '@heroicons/react/24/solid';
import {type TSenderState} from './MockChrome';

export type {TSenderState};

const Skeleton: FC<{className: string}> = ({className}) => <div className={`h-2 rounded-full bg-gray-100 ${className}`} />;

interface InboxPreviewProps {
  brand: IBrand;
  fromName: string;
  fromAddress: string;
  senderState: TSenderState;
  onOpenEmailBuilder: () => void;
}

/**
 * A stand-in for the invitation as it lands in an inbox. Only the From line is real: it tracks the
 * From name input as it is typed, and the address shows which mailbox actually sends today.
 */
export const InboxPreview: FC<InboxPreviewProps> = ({brand, fromName, fromAddress, senderState, onOpenEmailBuilder}) => {
  const logoUrl = brand.thumbnail_url || brand.full_logo_url;
  const customAddress = brand.email_domain && brand.email_local_part ? `${brand.email_local_part}@${brand.email_domain}`.toLowerCase() : '';

  return (
    <div>
      <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">In the recipient's inbox</h4>
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex gap-1.5 border-b border-gray-100 bg-gray-50 px-3 py-2.5">
          <div className="h-2.5 w-2.5 rounded-full bg-gray-300" />
          <div className="h-2.5 w-2.5 rounded-full bg-gray-300" />
          <div className="h-2.5 w-2.5 rounded-full bg-gray-300" />
        </div>

        <div className="border-b border-gray-100 px-4 py-3">
          <div className="-mx-2 rounded-lg bg-indigo-50/70 px-2 py-1.5 ring-1 ring-indigo-100">
            <span className="block text-[10px] font-semibold uppercase tracking-wide text-indigo-500">From</span>
            <span className="block break-words text-[13px] leading-snug">
              <b className="font-semibold text-gray-900">{fromName}</b>{' '}
              <span className={senderState === 'pending' ? 'text-amber-600' : 'text-gray-600'}>&lt;{fromAddress}&gt;</span>
              {senderState === 'active' && <CheckCircleIcon className="ml-1 inline size-3.5 -translate-y-px text-green-600" />}
            </span>
          </div>
          <div className="mt-2.5 flex items-center gap-3">
            <span className="w-11 text-[11px] text-gray-400">To</span>
            <Skeleton className="w-28" />
          </div>
          <div className="mt-2 flex items-center gap-3">
            <span className="w-11 text-[11px] text-gray-400">Subject</span>
            <Skeleton className="w-40" />
          </div>
        </div>

        {/* The body is a faint impression of the invitation; the builder behind the button owns it. */}
        <div className="relative">
          <div className="flex flex-col items-center gap-3 px-5 py-5 opacity-30 [mask-image:linear-gradient(to_bottom,black_40%,transparent)]">
            {logoUrl ? (
              <img src={logoUrl} alt="" className="h-7 max-w-[120px] object-contain" />
            ) : (
              <div className="h-7 w-20 rounded-md bg-gray-200" />
            )}
            <div className="h-9 w-full rounded-md" style={{background: brand.primary_color || '#654dcb'}} />
            <Skeleton className="w-11/12" />
            <Skeleton className="w-4/5" />
            <Skeleton className="w-2/3" />
            <div className="mt-1 h-6 w-28 rounded" style={{background: brand.secondary_color || '#55bc81'}} />
            <Skeleton className="mt-1 w-3/4" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <VerdocsButton label="Email Builder" size="small" onClick={onOpenEmailBuilder} />
          </div>
        </div>
      </div>

      <p className={`mt-2.5 text-xs leading-relaxed ${senderState === 'pending' ? 'text-amber-700' : 'text-gray-500'}`}>
        {senderState === 'default' && 'Sent from the shared Verdocs address. Set up a custom sender to use your own domain.'}
        {senderState === 'pending' &&
          `Still sending from notifications@verdocs.com. Switches to ${customAddress} once its DNS records verify.`}
        {senderState === 'active' && 'Sending from your own domain.'}
      </p>
    </div>
  );
};
